import React, { useMemo, useState } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import type { AgentCategoryId } from '../types/platform';
import { AGENT_CATEGORIES, AGENT_COUNT } from '../data/agents';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { AgentCard } from '../components/agents/AgentCard';
import { AgentIcon } from '../components/platform/AgentIcon';
import { ProcessCta } from '../components/platform/ProcessCta';
import { Reveal } from '../components/ui/Reveal';
import { cn } from '../src/lib/utils';
type Filter = 'all' | AgentCategoryId;
export function Agents() {
  usePageMeta({
    title: 'AI Agents for Due Diligence | DiligenceGPT™ Agent Catalog',
    description: `Browse ${AGENT_COUNT} specialized DiligenceGPT™ agents for intake, qualification, enrichment, communication, diligence, workflow, documents and monitoring.`,
    path: '/agents',
    keywords: ['AI due diligence agents', 'agentic workflow', 'diligence automation', 'vendor due diligence agent', 'investment memo agent'],
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Agents',
        item: canonicalUrl('/agents')
      }]
    }]
  });
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return AGENT_CATEGORIES.filter(category => filter === 'all' || category.id === filter).map(category => ({
      ...category,
      agents: category.agents.filter(agent => !needle ? true : [agent.name, agent.purpose, ...agent.inputs, ...agent.outputs, ...agent.useCases].join(' ').toLowerCase().includes(needle))
    })).filter(category => category.agents.length > 0);
  }, [filter, query]);
  const resultCount = groups.reduce((total, group) => total + group.agents.length, 0);
  return <>
      <main id="main" data-id="element-1389" data-mp="2hfuc03sdx47x">
        <PageHero headingId="agents-heading" breadcrumb="Agents" eyebrow={`${AGENT_COUNT} agents · 8 categories`} title="Specialized Agents for Every Stage of Due Process." description="Each agent does one job well. Combine them into the workflow your organization runs, with your team reviewing and deciding at every step." data-id="element-1390" data-mp="2hhf1vg3r7yzg" />

        <div className="sticky top-16 z-20 border-b border-hairline bg-ink/95 backdrop-blur-xl lg:top-[72px]" data-id="element-1391" data-mp="1xr1y01y2lpp4">
          <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:px-8" data-id="element-1392" data-mp="1trazfc2ay5a1">
            <div className="sf-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0 lg:flex-1 lg:flex-wrap lg:overflow-visible lg:pb-0" role="group" aria-label="Filter by category" data-id="element-1393" data-mp="1oplnlu799nkw">
              {[{
              id: 'all' as const,
              label: 'All'
            }, ...AGENT_CATEGORIES].map(item => {
              const active = filter === item.id;
              return <button key={item.id} type="button" onClick={() => setFilter(item.id)} aria-pressed={active} className={cn('inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime', active ? 'border-lime bg-lime text-white' : 'border-hairline bg-panel text-text-secondary hover:border-lime/40 hover:text-text-primary')} data-id="element-1394" data-mp="19mtz2tf7w20g">
                    {item.id !== 'all' ? <AgentIcon category={item.id} className="h-3.5 w-3.5" data-id="element-1395" data-mp="394gnf9fw5y6" /> : null}
                    {item.id === 'documents' ? 'Documents' : item.label.replace(/ Agents$/, '')}
                  </button>;
            })}
            </div>
            <form role="search" onSubmit={event => event.preventDefault()} className="relative w-full lg:w-72" data-id="element-1396" data-mp="29u8tzbmlptqe">
              <label htmlFor="agent-search" className="sr-only" data-id="element-1397" data-mp="2jm4uetc5tvxs">
                Search agents
              </label>
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" aria-hidden="true" data-id="element-1398" data-mp="2ev6f9x2wr3w4" />
              <input id="agent-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search agents, inputs or outputs" className="min-h-[44px] w-full rounded-lg border border-hairline bg-panel pl-9 pr-9 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-1399" data-mp="2sl9ui43ddxc2" />
              {query ? <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-1400" data-mp="13ahq2ybzwf62">
                  <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-1401" data-mp="2w50kj5zpdt0e" />
                </button> : null}
            </form>
          </div>
        </div>

        <div className="bg-surface" data-id="element-1402" data-mp="315ofu3uysw0f">
          <div className="mx-auto w-full max-w-[1320px] px-4 py-14 sm:px-6 lg:px-8 lg:py-16" data-id="element-1403" data-mp="2m6ujeqlq0qwl">
            <p className="text-sm text-text-secondary" aria-live="polite" data-id="element-1404" data-mp="2r5xno8tvmt3i">
              {resultCount === AGENT_COUNT ? `Showing all ${AGENT_COUNT} agents` : `${resultCount} of ${AGENT_COUNT} agents`}
            </p>

            {groups.length === 0 ? <div className="mt-8 rounded-2xl border border-dashed border-hairline bg-panel p-10 text-center" data-id="element-1405" data-mp="pe10g1ycjusw">
                <p className="text-base font-semibold text-text-primary" data-id="element-1406" data-mp="nyoxwivvzcq5">No agents match “{query}”.</p>
                <button type="button" onClick={() => {
              setQuery('');
              setFilter('all');
            }} className="mt-3 inline-flex min-h-[44px] items-center text-sm font-semibold text-lime hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-1407" data-mp="iwzlayt0mqzy">
                  Clear filters
                </button>
              </div> : null}

            {groups.map(category => <section key={category.id} id={category.id} aria-labelledby={`${category.id}-heading`} className="mt-12 scroll-mt-40 first-of-type:mt-8" data-id="element-1408" data-mp="swqdgtu3stwv">
                <Reveal className="flex flex-col gap-2 border-b border-hairline pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-1409" data-mp="1pfxq8xawmkcp">
                  <div className="flex items-start gap-4" data-id="element-1410" data-mp="1kltlmkp42oes">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-lime text-white" data-id="element-1411" data-mp="33bz2x6poqbwm">
                      <AgentIcon category={category.id} className="h-5 w-5" data-id="element-1412" data-mp="38blgjh9fdu5y" />
                    </span>
                    <div data-id="element-1413" data-mp="3cd6xpeszsuly">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-lime" data-id="element-1414" data-mp="3h9xi2ebtycd6">{category.label}</p>
                      <h2 id={`${category.id}-heading`} className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-text-primary" data-id="element-1415" data-mp="3ma1wzzx3az1a">
                        {category.headline}
                      </h2>
                      <p className="mt-1 text-sm text-text-secondary" data-id="element-1416" data-mp="3w04o1ukni2wz">{category.purpose}</p>
                    </div>
                  </div>
                  <span className="font-mono text-xs text-text-tertiary sm:pb-1" data-id="element-1417" data-mp="3xdid3aedp6h">{category.agents.length} agents</span>
                </Reveal>
                <ul className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-1418" data-mp="dsxarfpr65qh">
                  {category.agents.map(agent => <li key={agent.name} data-id="element-1419" data-mp="3ku4dsnb7ahwg">
                      <AgentCard agent={agent} category={category.id} data-id="element-1420" data-mp="3fwl1hsy2cf75" />
                    </li>)}
                </ul>
              </section>)}
          </div>
        </div>

        <ProcessCta placement="agents" title="Combine Agents Into Your Process." data-id="element-1421" data-mp="3pv1vkysat9xf" />
      </main>
    </>;
}